import { hasLeafChildren, hasTextChildren } from 'common/building-blocks/creation-types';
import type { BBModel, BBSymbolLink, ComponentTypes, StoryVersionType } from 'types/story';
import { validateStoryLinks, type StoryIssue } from './link-capability';

type StoryData = Pick<StoryVersionType['data'], 'elements' | 'steps' | 'symbols'>;
type IssueLocation =
	| { location: 'story'; inspectionCardId?: string; inspectionCardName?: string }
	| { location: 'card'; cardId: string; cardName: string };
type StructuralValidation = { issues: StoryIssue[]; suppressedElementIds: Set<string> };
type SymbolPlacement = { instanceId: string; cardId?: string; cardName?: string };

function isBlock(value: BBModel | BBSymbolLink): value is BBModel {
	return '_id' in value && 'type' in value;
}

function elementDetails(block: BBModel) {
	return {
		elementId: block._id,
		nodeId: block.uiConfig.nodeProps.id,
		name: block.uiConfig.editorProps.name || block.type,
		type: block.type,
	};
}

function walkBlocks(
	blocks: Array<BBModel | BBSymbolLink>,
	callback: (block: BBModel, ancestors: BBModel[]) => boolean | void,
	ancestors: BBModel[] = [],
	symbolCallback?: (link: BBSymbolLink) => void
) {
	for (const value of blocks) {
		if (!isBlock(value)) {
			symbolCallback?.(value);
			continue;
		}
		const stop = callback(value, ancestors);
		if (!stop && Array.isArray(value.children)) {
			walkBlocks(value.children, callback, [...ancestors, value], symbolCallback);
		}
	}
}

function descendantIds(block: BBModel): string[] {
	const ids: string[] = [];
	if (Array.isArray(block.children)) walkBlocks(block.children, child => void ids.push(child._id));
	return ids;
}

function findInstanceNode(instance: BBModel, masterId: string, masterNodeId: string): BBModel | null {
	if (masterId === masterNodeId) return instance;
	let found: BBModel | null = null;
	walkBlocks(instance.children as Array<BBModel | BBSymbolLink>, block => {
		if (block.symbol?.childId !== masterNodeId) return false;
		found = block;
		return true;
	});
	return found;
}

function inspectTrees(data: StoryData): StructuralValidation {
	const issues: StoryIssue[] = [];
	const suppressedElementIds = new Set<string>();
	const cards = data.steps.flatMap(step => step.cards);
	const firstCard = cards[0];
	const storyElementIds = new Set<string>();
	walkBlocks(data.elements, block => void storyElementIds.add(block._id));
	const placementsByMaster = new Map<string, SymbolPlacement[]>();
	const addPlacement = (link: BBSymbolLink, cardId?: string, cardName?: string) => {
		const placements = placementsByMaster.get(link.symbol.masterId) ?? [];
		if (!placements.some(item => item.instanceId === link.symbol.instanceId)) {
			placements.push({ instanceId: link.symbol.instanceId, cardId, cardName });
			placementsByMaster.set(link.symbol.masterId, placements);
		}
	};
	for (const card of cards) {
		walkBlocks(
			card.elements,
			() => undefined,
			[],
			link => addPlacement(link, card._id, card.name)
		);
	}
	walkBlocks(
		data.elements,
		() => undefined,
		[],
		link => addPlacement(link, firstCard?._id, firstCard?.name)
	);

	const recordIssue = ({
		block,
		ancestors,
		location,
		ownerKind,
		primaryNodeId = block.uiConfig.nodeProps.id,
		symbolMasterId,
	}: {
		block: BBModel;
		ancestors: BBModel[];
		location: IssueLocation;
		ownerKind: 'element' | 'symbol-master';
		primaryNodeId?: string;
		symbolMasterId?: string;
	}) => {
		issues.push({
			code: 'leaf-contains-elements',
			severity: 'error',
			message: `Element "${block._id}" of type "${block.type}" contains element children.`,
			...location,
			primaryElementId: block._id,
			primaryNodeId,
			primaryElementName: block.uiConfig.editorProps.name || block.type,
			primaryElementType: block.type as ComponentTypes,
			context: {},
			repairContext: {
				ownerKind,
				ancestors: ancestors.map(elementDetails),
				misplacedChildren: (block.children as BBModel[]).filter(isBlock).map(elementDetails),
				...(symbolMasterId ? { symbolMasterId } : {}),
			},
		});
	};

	const inspect = (roots: BBModel[], location: IssueLocation, skipIds?: ReadonlySet<string>) => {
		walkBlocks(roots, (block, ancestors) => {
			if (skipIds?.has(block._id)) return true;
			if (!(hasLeafChildren(block.type) || hasTextChildren(block.type)) || !Array.isArray(block.children)) {
				return false;
			}
			if (block.children.length === 0) return false;

			descendantIds(block).forEach(id => suppressedElementIds.add(id));
			recordIssue({ block, ancestors, location, ownerKind: 'element' });
			return true;
		});
	};

	inspect(data.elements, {
		location: 'story',
		inspectionCardId: firstCard?._id,
		inspectionCardName: firstCard?.name,
	});
	for (const card of cards)
		inspect(card.elements, { location: 'card', cardId: card._id, cardName: card.name }, storyElementIds);
	for (const [masterId, symbol] of Object.entries(data.symbols ?? {})) {
		walkBlocks([symbol.master], (block, ancestors) => {
			if (!(hasLeafChildren(block.type) || hasTextChildren(block.type)) || !Array.isArray(block.children)) {
				return false;
			}
			if (block.children.length === 0) return false;

			const masterDescendantIds = new Set(descendantIds(block));
			for (const instance of Object.values(symbol.instance)) {
				walkBlocks(instance.children as Array<BBModel | BBSymbolLink>, instanceNode => {
					if (instanceNode.symbol?.childId && masterDescendantIds.has(instanceNode.symbol.childId)) {
						suppressedElementIds.add(instanceNode._id);
					}
				});
			}

			const placement = placementsByMaster.get(masterId)?.[0];
			const instance = placement ? symbol.instance[placement.instanceId] : undefined;
			const instanceNode = instance ? findInstanceNode(instance, masterId, block._id) : null;
			recordIssue({
				block,
				ancestors,
				location: {
					location: 'story',
					inspectionCardId: placement?.cardId,
					inspectionCardName: placement?.cardName,
				},
				ownerKind: 'symbol-master',
				primaryNodeId: instanceNode?.uiConfig.nodeProps.id,
				symbolMasterId: masterId,
			});
			return true;
		});
	}

	return { issues, suppressedElementIds };
}

export function validateStoryStructure(data: StoryData): StoryIssue[] {
	return inspectTrees(data).issues;
}

export function validateStory(data: StoryData): StoryIssue[] {
	const structural = inspectTrees(data);
	const linkIssues = validateStoryLinks(data).filter(
		issue =>
			!structural.suppressedElementIds.has(issue.primaryElementId) &&
			!issue.relatedElementIds?.some(id => structural.suppressedElementIds.has(id))
	);
	return [...structural.issues, ...linkIssues];
}
